import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';


export interface PinnedAppData {
  id: string;
  position: 1 | 2 | 3 | 4;
}

export const usePinMode = () => {
  const queryClient = useQueryClient();
  // Always keep pin mode active - no need for state or toggle
  const isPinModeActive = true;

  const updatePinnedAppsMutation = useMutation({
    mutationFn: async ({ appId, position }: { appId: string; position: 1 | 2 | 3 | 4 | null }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      if (position === null) {
        // Remove pin - find which position this app is in and clear it
        const { data: currentUser } = await supabase
          .from('users')
          .select('app_1, app_2, app_3, app_4')
          .eq('id', user.id)
          .single();

        if (currentUser) {
          let columnToUpdate = null;
          if (currentUser.app_1 === appId) columnToUpdate = 'app_1';
          else if (currentUser.app_2 === appId) columnToUpdate = 'app_2';
          else if (currentUser.app_3 === appId) columnToUpdate = 'app_3';
          else if (currentUser.app_4 === appId) columnToUpdate = 'app_4';

          if (columnToUpdate) {
            const { error } = await supabase
              .from('users')
              .update({ [columnToUpdate]: null })
              .eq('id', user.id);

            if (error) throw error;
          }
        }
      } else {
        // Add or update pin
        const columnName = `app_${position}`;
        const { error } = await supabase
          .from('users')
          .update({ [columnName]: appId })
          .eq('id', user.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-pinned-apps'] });
      toast.success('Pin settings updated');
    },
    onError: (error) => {
      console.error('Failed to update pin:', error);
      toast.error('Failed to update pin settings');
    }
  });

  const swapPinnedPositionsMutation = useMutation({
    mutationFn: async ({ fromPos, toPos }: { fromPos: 1 | 2 | 3 | 4; toPos: 1 | 2 | 3 | 4 }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get current pinned apps
      const { data: currentPinned } = await supabase
        .from('users')
        .select('app_1, app_2, app_3, app_4')
        .eq('id', user.id)
        .single();

      if (!currentPinned) throw new Error('Failed to fetch current pinned apps');

      const fromApp = currentPinned[`app_${fromPos}`];
      const toApp = currentPinned[`app_${toPos}`];

      // Atomic swap - update both positions in single transaction
      const { error } = await supabase
        .from('users')
        .update({
          [`app_${fromPos}`]: toApp,
          [`app_${toPos}`]: fromApp
        })
        .eq('id', user.id);

      if (error) throw error;
    },
    onMutate: async ({ fromPos, toPos }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['user-pinned-apps'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['user-pinned-apps']);

      // Optimistically update the cache
      queryClient.setQueryData(['user-pinned-apps'], (old: any) => {
        if (!old) return old;
        
        const newData = { ...old };
        const temp = newData[`app_${fromPos}`];
        newData[`app_${fromPos}`] = newData[`app_${toPos}`];
        newData[`app_${toPos}`] = temp;
        return newData;
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['user-pinned-apps'], context.previousData);
      }
      console.error('Failed to swap positions:', err);
      toast.error('Failed to reorder apps');
    },
    onSuccess: () => {
      toast.success('Apps reordered successfully');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['user-pinned-apps'] });
    }
  });

  const handlePinApp = (appId: string, position?: 1 | 2 | 3 | 4) => {
    if (position) {
      // Pin to specific position
      updatePinnedAppsMutation.mutate({ appId, position });
    } else {
      // Get current pinned apps to determine if we should unpin or find next position
      const currentPinned = queryClient.getQueryData(['user-pinned-apps']) as any;
      
      if (currentPinned) {
        const isPinned = currentPinned.app_1 === appId || 
                        currentPinned.app_2 === appId || 
                        currentPinned.app_3 === appId ||
                        currentPinned.app_4 === appId;
        
        if (isPinned) {
          // Unpin the app
          updatePinnedAppsMutation.mutate({ appId, position: null });
        } else {
          // Pin to next available position
          let nextPosition: 1 | 2 | 3 | 4 = 1;
          if (currentPinned.app_1) {
            if (currentPinned.app_2) {
              nextPosition = currentPinned.app_3 ? 4 : 3;
            } else {
              nextPosition = 2;
            }
          }
          updatePinnedAppsMutation.mutate({ appId, position: nextPosition });
        }
      }
    }
  };

  const handlePositionSwap = (fromPos: 1 | 2 | 3 | 4, toPos: 1 | 2 | 3 | 4) => {
    if (fromPos !== toPos) {
      swapPinnedPositionsMutation.mutate({ fromPos, toPos });
    }
  };

  return {
    isPinModeActive,
    handlePinApp,
    handlePositionSwap,
    isUpdating: updatePinnedAppsMutation.isPending || swapPinnedPositionsMutation.isPending
  };
};
